
    import __i18nConfig from '@next-translate-root/i18n'
    import __appWithI18n from 'next-translate/appWithI18n'
    import 'intl-pluralrules';
import 'allsettled-polyfill';
import '@/styles/global.css';
import { configureKhetiAxiosInstance } from '@dehaat/kisan-app-bl/axios/configure';
import { DEFAULT_LANG } from '@dehaat/kisan-app-bl/constants/common';
import { Vendor } from '@dehaat/kisan-app-bl/models/Vendor';
import * as Sentry from '@sentry/nextjs';
import type { AppProps } from 'next/app';
import Head from 'next/head';
import Image from 'next/image';
import Router, { useRouter } from 'next/router';
import Script from 'next/script';
import { useEffect, useRef, useState } from 'react';
import { wrapper } from 'src/store';
import ErrorScreen from '@/components/ErrorBoundary';
import GoogleAnalytics from '@/components/GoogleAnalytics';
import MoengageScripts from '@/components/MoengageScripts';
import Spinner from '@/components/Spinner';
import { AGRICENTRAL_CLIENT_ID, BOOKING_DETAIL, BOOKING_MANDATORY_DATA, BOOKING_OPTIONAL_DATA, CLIENT_NAME_COOKIE, COOKIE_MAX_AGE_1YEAR, CURRENT_LOCATION_KEY, CURRENT_VENDOR_KEY, IS_CREDIT_USER, LAT_LNG_COOKIE_NAME, NAVIGATION_FROM_APP_CODE, ONE_CLICK_APP_SOURCE, SERVICE_CART, SERVICE_ICON_URL, SERVICE_ID, SERVICE_NAME, SERVICE_VENDOR_ID, SERVICE_VENDOR_NAME, VENDOR_ID_COOKIE_NAME, } from '@/constants/common';
import AuthProvider from '@/context/AuthProvider';
import CampaignProvider from '@/context/CampaignProvider';
import CartProvider from '@/context/CartProvider';
import ErrorModalProvider from '@/context/ErrorModalProvider';
import InsuranceProvider from '@/context/InsuranceProvider';
import UserProvider from '@/context/UserProvider';
import { useAppDispatch, useAppSelector } from '@/hooks/reduxHooks';
import useTriggerEvents from '@/hooks/useTriggerEvents';
import { APP_CODE_TYPE } from '@/models/Auth';
import CurrentLocation from '@/models/Location';
import { fetchHyperlocalData, fetchOndcVendors, selectLatLng, setCurrentLocation, } from '@/store/features/locationSlice';
import { setBookingDetail, setMandatoryFieldData, setOptionalFieldData, setServiceCart, setServiceIconUrl, setServiceId, setServiceName, setServiceVendorId, setServiceVendorName, } from '@/store/features/serviceSlice';
import CustomEventTypes from '@/utils/analytics/customEventTypes';
// import { trackCustomEvent } from '@/utils/analytics/googleAnalytics'
import { initMixpanel } from '@/utils/analytics/mixpanel';
import { trackPageView } from '@/utils/analytics/trackProductEvent';
import { syncSessionUtmToDataLayer } from '@/utils/analytics/utmParams';
import { khetiConfig } from '@/utils/axios/khetiConfig';
import { getGeoLocationValues } from '@/utils/google';
import { getCookieValue, getLocalStorageKey, isAgricentralUser, isDigiAcreUser, isProductionBuild, isSessionStorageAvailable, setLocalStorageKey, } from '@/utils/helper';
import getClientCode from '@/utils/helpers/getClientCode';
import { multiLocalStorageSync } from '@/utils/helpers/handleServiceCartSyncing';
import { askNotificationPermission } from '@/utils/helpers/promptNotifications';
configureKhetiAxiosInstance(khetiConfig);
function MyApp({ Component, pageProps }: AppProps<{
    title?: string;
}>) {
    const router = useRouter();
    const { asPath, replace, pathname, locale, query: { v }, } = router;
    const dispatch = useAppDispatch();
    const latLng = useAppSelector(selectLatLng);
    const forCreditOrder = getLocalStorageKey(IS_CREDIT_USER) == '1';
    const { trackCustomEvent } = useTriggerEvents();
    const [isRouteChanging, setIsRouteChanging] = useState(false);
    const hasTrackedInitialPageView = useRef(false);
    const isLocationSyncing = useRef(false);
    // `latLng|v` we last asked hyperlocal for, so a `v` that isn't serviceable
    // at this location is tried once instead of on every route change.
    const lastRequestedVendor = useRef<string>();
    const isFromAgricentral = isAgricentralUser();
    const clientCode = (typeof window !== 'undefined' &&
        isSessionStorageAvailable(window) &&
        sessionStorage?.getItem(ONE_CLICK_APP_SOURCE)) ||
        getCookieValue(CLIENT_NAME_COOKIE) ||
        getClientCode(getCookieValue(NAVIGATION_FROM_APP_CODE) as APP_CODE_TYPE);
    // Set CLIENT_NAME cookie for AGRICENTRAL_CLIENT_ID when the user is on the AgriCentral domain
    useEffect(() => {
        if (isFromAgricentral && clientCode !== AGRICENTRAL_CLIENT_ID) {
            document.cookie = `${CLIENT_NAME_COOKIE}=${AGRICENTRAL_CLIENT_ID}; max-age=${COOKIE_MAX_AGE_1YEAR}; path=/`;
        }
    }, [clientCode, isFromAgricentral]);
    // const loadingHyperlocalData = useAppSelector(selectLoadingHyperlocalData)
    // Initialize Mixpanel for tracking analytics when component mounts
    useEffect(() => {
        // Defer Mixpanel + Firebase Remote Config off the critical path: init fires
        // several remote-config network calls and parses a heavy SDK. mixpanel.ts
        // queues events until init runs, so delaying it loses no tracking.
        const start = () => initMixpanel();
        if (typeof window.requestIdleCallback === 'function') {
            const id = window.requestIdleCallback(start, { timeout: 3000 });
            return () => window.cancelIdleCallback?.(id);
        }
        const timer = window.setTimeout(start, 2000);
        return () => window.clearTimeout(timer);
    }, []);
    useEffect(() => {
        const resolveClientCode = () => (typeof window !== 'undefined' &&
            isSessionStorageAvailable(window) &&
            sessionStorage?.getItem(ONE_CLICK_APP_SOURCE)) ||
            getCookieValue(CLIENT_NAME_COOKIE) ||
            getClientCode(getCookieValue(NAVIGATION_FROM_APP_CODE) as APP_CODE_TYPE) ||
            '';
        const trackPageViewEvent = (url: string) => {
            syncSessionUtmToDataLayer(url);
            trackPageView(url, resolveClientCode());
        };
        const fireInitialPageView = () => {
            if (hasTrackedInitialPageView.current)
                return;
            hasTrackedInitialPageView.current = true;
            trackPageViewEvent(window.location.href);
        };
        const onMoengageConfigured = () => fireInitialPageView();
        window.addEventListener('moengage:configured', onMoengageConfigured);
        // Script may dispatch moengage:configured before this effect runs.
        if (typeof window.Moengage?.track_event === 'function') {
            fireInitialPageView();
        }
        const initialPageViewTimer = window.setTimeout(fireInitialPageView, 2000);
        router.events.on('routeChangeComplete', trackPageViewEvent);
        return () => {
            window.removeEventListener('moengage:configured', onMoengageConfigured);
            window.clearTimeout(initialPageViewTimer);
            router.events.off('routeChangeComplete', trackPageViewEvent);
        };
    }, [router.events]);
    // Trigger notification permission prompt after 40 seconds if not already granted
    useEffect(() => {
        const showAfterMilliseconds = 40000; // Configurable value for showing notification prompt
        if ('Notification' in window && Notification.permission === 'default') {
            setTimeout(() => {
                askNotificationPermission(trackCustomEvent);
                trackCustomEvent(CustomEventTypes.NOTIFICATION_HARD_ASK_SHOWN, showAfterMilliseconds.toString());
            }, showAfterMilliseconds);
        }
    }, []);
    useEffect(() => {
        // Re-run getServerSideProps with the synced cookies. The user may have
        // navigated while the sync was in flight, so refresh the page they are on
        // now, not the one this effect started on.
        const refreshPageData = () => Router.replace(Router.asPath);
        const getGeoLocation = async (latitude: number, longitude: number) => {
            const locationDetails = await getGeoLocationValues(latitude, longitude);
            dispatch(setCurrentLocation(locationDetails));
            await dispatch(fetchHyperlocalData({
                latLng: locationDetails.latLng,
                language: locale || DEFAULT_LANG,
                vendorId: v ? (v as string) : getCookieValue(VENDOR_ID_COOKIE_NAME),
                isCSCUser: isDigiAcreUser(),
                forCreditOrder,
            }));
            setLocalStorageKey(CURRENT_LOCATION_KEY, locationDetails);
            document.cookie = `${LAT_LNG_COOKIE_NAME}=${locationDetails.latLng}; max-age=${COOKIE_MAX_AGE_1YEAR}; path=/`;
            refreshPageData();
        };
        const handleLocationSyncing = async () => {
            const latLngCookie = getCookieValue(LAT_LNG_COOKIE_NAME);
            const currentLocation = getLocalStorageKey<CurrentLocation, null>(CURRENT_LOCATION_KEY);
            const vendorInCookie = getCookieValue(VENDOR_ID_COOKIE_NAME);
            const currentVendor = getLocalStorageKey<Vendor | null, null>(CURRENT_VENDOR_KEY);
            // getGeoLocation already fetches hyperlocal data with `v`/cookie vendor
            let hyperlocalSynced = false;
            // If latLng in cookie is different from currentLatLng
            if (latLngCookie && latLngCookie !== currentLocation?.latLng) {
                const latLngArr = latLngCookie.split(',');
                if (+latLngArr[0] !== 0.0 || +latLngArr[1] !== 0.0) {
                    await getGeoLocation(+latLngArr[0], +latLngArr[1]);
                    hyperlocalSynced = true;
                }
            }
            // to sync redux locationSlice with LS
            else if (currentLocation && !latLng) {
                const coordinates = currentLocation.latLng.split(',');
                await getGeoLocation(+coordinates[0], +coordinates[1]);
                hyperlocalSynced = true;
            }
            const requestedVendor = v
                ? `${getCookieValue(LAT_LNG_COOKIE_NAME)}|${v}`
                : undefined;
            if (hyperlocalSynced) {
                lastRequestedVendor.current = requestedVendor;
                return;
            }
            // fetchHyperlocalData writes 'null' to the cookie when no vendor serves
            // the location; that is in sync with a null vendor in LS.
            const cookieVendorId = vendorInCookie && vendorInCookie !== 'null' ? vendorInCookie : undefined;
            // If vendorId in cookie or v is different from currentVendorId
            if (latLngCookie &&
                (cookieVendorId != currentVendor?.id ||
                    (v &&
                        v != currentVendor?.id &&
                        lastRequestedVendor.current !== requestedVendor))) {
                lastRequestedVendor.current = requestedVendor;
                await dispatch(fetchHyperlocalData({
                    latLng: latLngCookie,
                    language: locale || DEFAULT_LANG,
                    vendorId: v ? (v as string) : cookieVendorId,
                    isCSCUser: isDigiAcreUser(),
                    forCreditOrder,
                }));
                // To handle infinite re-loading in case of 500 and 404
                // proper fix for all pages is due.
                if (!Router.pathname.includes('/500') &&
                    !Router.pathname.includes('/404')) {
                    refreshPageData();
                }
            }
        };
        // `replace` gets a new identity on every route change, so this effect
        // re-runs while a sync (which ends in a page refresh) is still in flight.
        if (isLocationSyncing.current)
            return;
        isLocationSyncing.current = true;
        handleLocationSyncing().finally(() => {
            isLocationSyncing.current = false;
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, locale, replace, v]);
    // ONDC providers are fetched only while the user is on ONDC pages
    const isOndcRoute = pathname.startsWith('/ondc');
    useEffect(() => {
        if (isOndcRoute && latLng) {
            dispatch(fetchOndcVendors({ latLng, language: locale || DEFAULT_LANG }));
        }
    }, [dispatch, isOndcRoute, latLng, locale]);
    // Syncing redux data with LS for service cart
    useEffect(() => {
        multiLocalStorageSync([
            { key: SERVICE_ID, defaultValue: undefined },
            { key: SERVICE_NAME, defaultValue: undefined },
            { key: SERVICE_ICON_URL, defaultValue: undefined },
            { key: SERVICE_CART, defaultValue: null },
            { key: BOOKING_MANDATORY_DATA, defaultValue: null },
            { key: BOOKING_OPTIONAL_DATA, defaultValue: null },
            { key: BOOKING_DETAIL, defaultValue: null },
            { key: SERVICE_VENDOR_ID, defaultValue: undefined },
            { key: SERVICE_VENDOR_NAME, defaultValue: undefined },
        ], (key, value) => {
            switch (key) {
                case SERVICE_ID:
                    value && dispatch(setServiceId(value));
                    break;
                case SERVICE_NAME:
                    value && dispatch(setServiceName(value));
                    break;
                case SERVICE_ICON_URL:
                    value && dispatch(setServiceIconUrl(value));
                    break;
                case SERVICE_CART:
                    value && dispatch(setServiceCart(value));
                    break;
                case BOOKING_MANDATORY_DATA:
                    value && dispatch(setMandatoryFieldData(value));
                    break;
                case BOOKING_OPTIONAL_DATA:
                    value && dispatch(setOptionalFieldData(value));
                    break;
                case BOOKING_DETAIL:
                    value && dispatch(setBookingDetail(value));
                    break;
                case SERVICE_VENDOR_ID:
                    value && dispatch(setServiceVendorId(value));
                    break;
                case SERVICE_VENDOR_NAME:
                    value && dispatch(setServiceVendorName(value));
                    break;
                default:
                    break;
            }
        });
    }, [dispatch]);
    // if (loadingHyperlocalData) return <Spinner />
    return (<Sentry.ErrorBoundary fallback={<ErrorScreen />}>
      <Head>
        {pageProps.title ? <title>{pageProps.title}</title> : null}
        <meta name="viewport" content="width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=0"/>
      </Head>
      <AuthProvider>
        <UserProvider>
          <ErrorModalProvider>
            <CartProvider>
              <CampaignProvider>
                <InsuranceProvider>
                  <Component {...pageProps}/>
                  <GoogleAnalytics />
                  <MoengageScripts />
                  <Script id="facebook-pixel" strategy="lazyOnload">
                    {`
                  !function(f,b,e,v,n,t,s)
                  {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
                  n.callMethod.apply(n,arguments):n.queue.push(arguments)};
                  if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
                  n.queue=[];t=b.createElement(e);t.async=!0;
                  t.src=v;s=b.getElementsByTagName(e)[0];
                  s.parentNode.insertBefore(t,s)}(window, document,'script',
                  'https://connect.facebook.net/en_US/fbevents.js');
                  fbq('init', '1551884201879250');
                  fbq('track', 'PageView');
                  `}
                  </Script>
                  <Script id="google-tag-manager" strategy="lazyOnload">
                    {`
        (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
        new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
        j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
        'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
        })(window,document,'script','dataLayer','${process.env.NEXT_PUBLIC_GTM_KEY}');
      `}
                  </Script>
                  {isProductionBuild && (<Script id="sharechat-base-pixel" strategy="lazyOnload">
                      {`(function (w, d, s, l, i) {
              w['scSdkId'] = i;
              w[l] = w[l] || []
              w.scq = function (eventName, eventType, p) {
              var props = p || {}
                w[l].push({ eventName: eventName, eventType: eventType, meta: props, eventFireTs: Date.now() })
              };
              w.scq("PAGE_VIEW", "AUTO", {
                pageUrl: w.location.href
              });
              var scr = d.createElement(s);
              scr.type = 'text/javascript';
              scr.async = true;
              scr.src = 'https://sc-events-sdk.sharechat.com/web-sdk.js';
              var x = d.getElementsByTagName(s)[0];
              x.parentNode.insertBefore(scr, x);
            })(window, document, "script", "scLayer", "BWYVh_xkeq");`}
                    </Script>)}
                  <noscript>
                    <Image height="1" width="1" src="https://www.facebook.com/tr?id=1551884201879250&ev=PageView&noscript=1" alt="" style={{ display: 'none' }}/>
                  </noscript>
                  <noscript dangerouslySetInnerHTML={{
            __html: `<iframe src="https://www.googletagmanager.com/ns.html?id=${process.env.NEXT_PUBLIC_GTM_KEY}" height="0" width="0" style="display: none; visibility: hidden;" />`,
        }}/>
                </InsuranceProvider>
              </CampaignProvider>
            </CartProvider>
          </ErrorModalProvider>
        </UserProvider>
      </AuthProvider>
    </Sentry.ErrorBoundary>);
}
const __Next_Translate__Page__1a1a1556c8d__ = wrapper.withRedux(MyApp);

    export default __appWithI18n(__Next_Translate__Page__1a1a1556c8d__, {
      ...__i18nConfig,
      isLoader: true,
      skipInitialProps: false,
      loadLocaleFrom: __i18nConfig.loadLocaleFrom || ((l, n) => import(`@next-translate-root/locales/${l}/${n}`).then(m => m.default)),
    });
  